const KINDS = ["critical", "device", "connection"];

const isPlaying = (sound) => {
  try {
    return typeof sound?.playing === "function" ? !!sound.playing() : false;
  } catch (e) {
    return false;
  }
};

const sameKind = (a, b) => a.active === b.active;

const sameSnapshot = (a, b) =>
  sameKind(a.critical, b.critical) &&
  sameKind(a.device, b.device) &&
  a.connection.active === b.connection.active &&
  a.connection.lost === b.connection.lost;

export function createAlarmControl({
  setTimer = (callback, ms) => setTimeout(callback, ms),
  endGraceMs = 1000,
} = {}) {
  const entries = new Map();
  const states = new WeakMap();
  const listeners = new Set();
  const conditions = { connection: false };

  const buildSnapshot = () => {
    const kinds = {};
    KINDS.forEach((kind) => {
      kinds[kind] = { active: false };
    });
    entries.forEach((kind, sound) => {
      const state = states.get(sound);
      if (!state || !kinds[kind]) return;
      if (state.sounding || state.blocked) kinds[kind].active = true;
    });
    return {
      critical: kinds.critical,
      device: kinds.device,
      connection: {
        active: kinds.connection.active,
        lost: conditions.connection,
      },
    };
  };

  let snapshot = buildSnapshot();

  const emit = () => {
    const next = buildSnapshot();
    if (sameSnapshot(snapshot, next)) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const notify = (sound) => {
    if (entries.has(sound)) emit();
  };

  const instrument = (sound) => {
    if (states.has(sound)) return states.get(sound);
    const state = {
      sounding: false,
      blocked: false,
      stopIntent: false,
    };
    states.set(sound, state);

    const stop = sound.stop;
    sound.stop = function (...args) {
      const internal = args[1] === true;
      if (!internal) {
        state.stopIntent = true;
        state.blocked = false;
      }
      const result = stop.apply(this, args);
      if (!internal) {
        state.sounding = isPlaying(sound);
        notify(sound);
      }
      return result;
    };

    sound.on("play", () => {
      if (state.stopIntent) {
        sound.stop();
        return;
      }
      state.blocked = false;
      state.sounding = true;
      notify(sound);
    });
    sound.on("stop", () => {
      state.sounding = isPlaying(sound);
      notify(sound);
    });
    sound.on("end", () => {
      setTimer(() => {
        if (isPlaying(sound)) return;
        state.sounding = false;
        notify(sound);
      }, endGraceMs);
    });
    sound.on("playerror", () => {
      state.blocked = !state.stopIntent;
      state.sounding = isPlaying(sound);
      notify(sound);
    });

    return state;
  };

  const registerAlarm = (sound, kind) => {
    if (!sound || !KINDS.includes(kind)) return sound;
    instrument(sound);
    if (entries.get(sound) !== kind) {
      entries.set(sound, kind);
      emit();
    }
    return sound;
  };

  const unregisterAlarm = (sound) => {
    if (entries.delete(sound)) emit();
  };

  const markAlarmStart = (sound) => {
    const state = states.get(sound);
    if (!state) return;
    state.stopIntent = false;
    notify(sound);
  };

  const hasStopIntent = (sound) => !!states.get(sound)?.stopIntent;

  const setAlarmCondition = (name, value) => {
    if (!(name in conditions) || conditions[name] === !!value) return;
    conditions[name] = !!value;
    emit();
  };

  return {
    registerAlarm,
    unregisterAlarm,
    markAlarmStart,
    hasStopIntent,
    setAlarmCondition,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };
}

const alarmControl = createAlarmControl();

export const registerAlarm = alarmControl.registerAlarm;
export const unregisterAlarm = alarmControl.unregisterAlarm;
export const markAlarmStart = alarmControl.markAlarmStart;
export const hasStopIntent = alarmControl.hasStopIntent;
export const setAlarmCondition = alarmControl.setAlarmCondition;
export const subscribeAlarms = alarmControl.subscribe;
export const getAlarmSnapshot = alarmControl.getSnapshot;
